import { api } from './client'
import type { ContractDeliveryOrder } from './contract-delivery-api'
import type { SalesOrder } from './sales-order-api'
import type { Vehicle } from './vehicle-api'

export type DeliveryTripStatus = 'active' | 'closed'

export interface DeliveryTrip {
    id: string
    trip_code: string
    driver_id: string
    driver_name?: string
    vehicle_id: string
    vehicle?: Vehicle
    status: DeliveryTripStatus
    started_at: string
    ended_at?: string | null
    note?: string | null
    created_at: string
    updated_at: string
    orders?: SalesOrder[]
    // Đơn giao hợp đồng gộp chung chuyến (BE hydrate qua ContractOrderTripPort).
    contract_orders?: ContractDeliveryOrder[]
}

export interface ListTripsParams {
    page?: number
    per_page?: number
    driver_id?: string
    vehicle_id?: string
    status?: DeliveryTripStatus
    from?: string
    to?: string
}

export interface EndTripRequest {
    note?: string
}

/**
 * Payload cho POST /delivery-trips/:id/force-end. Note optional (max 500 ký
 * tự), chỉ để audit — status_log luôn có sẵn marker "force_end:".
 */
export interface ForceEndTripRequest {
    note: string
}

export const deliveryTripApi = {
    getActive: () => api.get<DeliveryTrip>('/delivery-trips/active'),
    /** Admin lookup — trip đang chạy của 1 vehicle cụ thể. `data: null` khi
     *  vehicle không có active trip. Yêu cầu permission `vehicles.read`. */
    getActiveByVehicle: (vehicleId: string) =>
        api.get<DeliveryTrip | null>(
            `/delivery-trips/active-by-vehicle/${vehicleId}`,
        ),
    /** Chuyến đang chạy của 1 tài xế cụ thể — popup "bắt đầu giao hàng" dùng
     *  khi văn phòng bấm hộ bác tài không có điện thoại. `data: null` khi tài
     *  xế chưa có chuyến. Yêu cầu permission `delivery_trips.force_end`. */
    getActiveByDriver: (driverId: string) =>
        api.get<DeliveryTrip | null>(
            `/delivery-trips/active-by-driver/${driverId}`,
        ),
    endActive: (data?: EndTripRequest) =>
        api.post<DeliveryTrip>('/delivery-trips/end', data ?? {}),
    /** Admin force-end trip — dùng khi tài xế quên bấm end. Yêu cầu
     *  permission `delivery_trips.force_end`. */
    forceEnd: (id: string, data: ForceEndTripRequest) =>
        api.post<DeliveryTrip>(`/delivery-trips/${id}/force-end`, data),
    list: (params?: ListTripsParams) =>
        api.get<DeliveryTrip[]>('/delivery-trips', { params }),
    getById: (id: string) => api.get<DeliveryTrip>(`/delivery-trips/${id}`),
}
